if (!customElements.get('navigation-dialog-v2')) {
  /**
   * Mosaic navigation wall: dialog open/close, in-page anchor jump.
   *
   * Payment SVGs from `payment_type_svg_tag` use hardcoded paint-server ids
   * (e.g. `pi-bancontact-a`). The card keeps that markup in a `<template>`
   * (inert, not in the live document). Opening clones into the dialog and
   * suffixes ids on the clone only, so Bancontact gradients do not collide
   * with the footer icons or a hidden source node.
   */
  class NavigationDialogV2 extends HTMLElement {
    connectedCallback() {
      if (this.isBound) this.unbind();

      this.sectionId = this.dataset.sectionId || '';
      this.dialogEl = this.querySelector('[data-nd-dialog-el]');
      this.dialogTitle = this.querySelector('[data-nd-dialog-title]');
      this.dialogBody = this.querySelector('[data-nd-dialog-body]');
      this.closeBtn = this.querySelector('[data-nd-dialog-close]');
      this.dialogPaintUid = this.dialogPaintUid || 0;

      this.onRootClick = (event) => this.handleRootClick(event);
      this.onCloseClick = () => this.closeDialog();
      this.onDialogClick = (event) => this.handleDialogClick(event);
      this.onDialogClose = () => this.clearDialogBody();
      this.onKeydown = (event) => this.handleKeydown(event);

      this.addEventListener('click', this.onRootClick);
      this.closeBtn?.addEventListener('click', this.onCloseClick);
      this.dialogEl?.addEventListener('click', this.onDialogClick);
      this.dialogEl?.addEventListener('close', this.onDialogClose);
      document.addEventListener('keydown', this.onKeydown);

      this.isBound = true;
    }

    disconnectedCallback() {
      this.unbind();
    }

    /**
     * Remove listeners so theme editor re-renders can reconnect cleanly.
     */
    unbind() {
      this.removeEventListener('click', this.onRootClick);
      this.closeBtn?.removeEventListener('click', this.onCloseClick);
      this.dialogEl?.removeEventListener('click', this.onDialogClick);
      this.dialogEl?.removeEventListener('close', this.onDialogClose);
      document.removeEventListener('keydown', this.onKeydown);
      this.clearDialogBody();
      this.isBound = false;
    }

    /**
     * @param {MouseEvent} event
     */
    handleRootClick(event) {
      const dialogCard = event.target.closest('[data-nd-dialog]');
      if (dialogCard && this.contains(dialogCard)) {
        event.preventDefault();
        this.openDialog(dialogCard);
        return;
      }

      const anchorCard = event.target.closest('[data-nd-anchor]');
      if (anchorCard && this.contains(anchorCard)) {
        event.preventDefault();
        this.jumpToAnchor(anchorCard.dataset.anchorObj, anchorCard.dataset.forceAnchor === 'true');
      }
    }

    /**
     * @param {HTMLElement} card
     */
    openDialog(card) {
      if (!this.dialogEl || !this.dialogBody) return;

      this.clearDialogBody();
      const fragment = this.cloneDialogSource(card);
      this.uniquifySvgPaintIds(fragment);
      this.dialogBody.appendChild(fragment);

      if (this.dialogTitle) {
        this.dialogTitle.textContent = card.dataset.ndTitle || '';
      }

      if (typeof this.dialogEl.showModal === 'function') {
        if (!this.dialogEl.open) this.dialogEl.showModal();
      } else {
        this.dialogEl.setAttribute('open', '');
      }

      this.dialogEl.focus({ preventScroll: true });
    }

    /**
     * Clone card dialog markup from an inert `<template>`. Never moves the
     * source nodes, so Shopify ids on the template stay unchanged.
     *
     * @param {HTMLElement} card
     * @returns {DocumentFragment}
     */
    cloneDialogSource(card) {
      const fragment = document.createDocumentFragment();
      const source = card.querySelector('[data-nd-dialog-source]');
      if (!source) return fragment;

      if (source instanceof HTMLTemplateElement) {
        fragment.appendChild(source.content.cloneNode(true));
        return fragment;
      }

      fragment.appendChild(source.cloneNode(true));
      return fragment;
    }

    /**
     * Drop the cloned dialog tree. Idempotent: safe on double Escape / close.
     */
    clearDialogBody() {
      this.dialogBody?.replaceChildren();
    }

    closeDialog() {
      if (!this.dialogEl) return;

      if (typeof this.dialogEl.close === 'function') {
        if (this.dialogEl.open) this.dialogEl.close();
        return;
      }

      if (this.dialogEl.hasAttribute('open')) {
        this.dialogEl.removeAttribute('open');
        this.clearDialogBody();
      }
    }

    /**
     * Close when clicking the backdrop (the dialog element itself).
     * @param {MouseEvent} event
     */
    handleDialogClick(event) {
      if (event.target === this.dialogEl) {
        this.closeDialog();
      }
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeDialog();
      }
    }

    /**
     * Suffix paint-server ids inside cloned payment SVGs only.
     * Scoped to the dialog body so card icons and the close button are untouched.
     *
     * @param {ParentNode} root
     */
    uniquifySvgPaintIds(root) {
      if (!root) return;

      this.dialogPaintUid += 1;
      const openUid = this.dialogPaintUid;
      const svgs = root.querySelectorAll('svg');

      svgs.forEach((svg, svgIndex) => {
        const idMap = new Map();
        const suffix = `nd${openUid}-${svgIndex}`;

        svg.querySelectorAll('[id]').forEach((el) => {
          const oldId = el.getAttribute('id');
          if (!oldId) return;
          const newId = `${oldId}-${suffix}`;
          idMap.set(oldId, newId);
          el.setAttribute('id', newId);
        });

        if (idMap.size === 0) return;

        const nodes = [svg, ...svg.querySelectorAll('*')];
        nodes.forEach((node) => {
          if (!node.attributes) return;
          Array.from(node.attributes).forEach((attr) => {
            const rewritten = this.rewriteSvgIdRefs(attr.value, idMap);
            if (rewritten !== attr.value) {
              node.setAttribute(attr.name, rewritten);
            }
          });
        });
      });
    }

    /**
     * Rewrite url(#id), #id, and token lists (aria-labelledby) using exact
     * captured ids so `pi-bancontact` does not clobber `pi-bancontact-a`.
     *
     * @param {string} value
     * @param {Map<string, string>} idMap
     * @returns {string}
     */
    rewriteSvgIdRefs(value, idMap) {
      if (!value || idMap.size === 0) return value;

      const withUrl = value.replace(/url\((['"]?)#([^)'"]+)\1\)/g, (match, quote, id) => {
        const mapped = idMap.get(id);
        if (!mapped) return match;
        return quote ? `url(${quote}#${mapped}${quote})` : `url(#${mapped})`;
      });
      if (withUrl !== value) return withUrl;

      if (value.charAt(0) === '#' && idMap.has(value.slice(1))) {
        return `#${idMap.get(value.slice(1))}`;
      }

      if (value.includes('(') || value.charAt(0) === '#') return value;

      const parts = value.split(/(\s+)/);
      let changed = false;
      const rewritten = parts.map((part) => {
        if (!part || /^\s+$/.test(part)) return part;
        const mapped = idMap.get(part);
        if (!mapped) return part;
        changed = true;
        return mapped;
      });

      return changed ? rewritten.join('') : value;
    }

    /**
     * Port of handle_navigationGetElementId from navigation-dialog.
     * @param {string} sessionId
     * @param {boolean} isForceAnchor
     */
    jumpToAnchor(sessionId, isForceAnchor) {
      if (!sessionId) return;

      const prefix = 'shopify-section-template';
      let elementId = '';

      if (sessionId.includes(prefix)) {
        const currentSessionPart = sessionId.split('__')[1] || '';
        elementId = `shopify-section-${this.templatePrefix()}__${currentSessionPart}`;
      } else {
        elementId = `shopify-section-${this.templatePrefix()}__${sessionId}`;
      }

      const el = document.getElementById(elementId);
      if (!el) return;

      const scrollToPos = el.offsetTop;

      if (!isForceAnchor) {
        window.scrollTo({ top: scrollToPos, behavior: 'smooth' });
        return;
      }

      window.scrollTo({ top: scrollToPos, behavior: 'auto' });
      setTimeout(() => {
        const el2 = document.getElementById(elementId);
        if (el2) {
          window.scrollTo({ top: el2.offsetTop, behavior: 'auto' });
        }
      }, 200);
    }

    /**
     * @returns {string}
     */
    templatePrefix() {
      return (this.sectionId || '').split('__')[0];
    }
  }

  customElements.define('navigation-dialog-v2', NavigationDialogV2);
}
